import { useMemo } from 'react'

import { useComposerStore } from '../hooks/useComposerStore'
import { useChannelVariantReadsActive } from './useChannelVariantReadsActive'
import { useLegacyChannelData } from './useLegacyChannelData'
import type { DraftView } from './types'

/**
 * TRANSITIONAL: fuses Zustand state with legacy Flux channel data; content
 * fields shrink toward Zustand-only as fields migrate; deletes when all
 * per-channel overrides land in the store.
 *
 * The content fields come from one of three sources, chosen per render:
 * - the channel's Zustand variant (`channelVariants[channelId]`), when
 *   channel-variant reads are active (see `useChannelVariantReadsActive`) and
 *   the composer is in customize mode. If the variant hasn't synced yet
 *   (channel just selected, mirror not settled), this falls back to the
 *   legacy snapshot — never omni: customize mode must not serve the shared
 *   channel-less omni content as a channel's own.
 * - Zustand omni (`state.omni`), when `omniBridgeReady` — in omni mode, or
 *   whenever channel-variant reads are inactive.
 * - otherwise the legacy Flux snapshot — the pre-seed fallback until the
 *   bridge has seeded.
 *
 * The metadata scalars (`updateType`, `isReminder`, `title`,
 * `enabledAttachmentType`), the per-service settings bags (`channelData`,
 * `sourceLink`, `retweet`, `selectedStickers`), and `threadItems` come from
 * the channel's Zustand variant whenever channel-variant reads are active and
 * the variant exists — in BOTH omni and customize mode, unlike content: they
 * live on the per-service Flux draft regardless of mode (see
 * `useLegacyChannelData.buildSnapshot`), so there is no stale-service-draft
 * risk to gate against. `places` and `document` always come from the Flux
 * snapshot by design, not as a migration gap: `places` has no UI caller left,
 * and `document` already flows through the migrated `assets` field as a
 * completed `OmniAsset`. `selectedTagIds` always comes from the store.
 *
 * @see DraftView in ./types for the returned shape and its content vs
 *   channel-specific field split
 * @see ChannelVariantContent in ../postSlice/types for the migrated content
 *   fields
 */
export function useDraftView(channelId: string): DraftView {
  const legacy = useLegacyChannelData(channelId)
  const channelReadsActive = useChannelVariantReadsActive()

  const bridgeReady = useComposerStore((state) => state.omniBridgeReady)
  const omniText = useComposerStore((state) => state.omni.text)
  const omniUrls = useComposerStore((state) => state.omni.urls)
  const omniUnshortenedUrls = useComposerStore(
    (state) => state.omni.unshortenedUrls,
  )
  const omniAssets = useComposerStore((state) => state.omni.assets)
  const omniAiAssisted = useComposerStore((state) => state.omni.aiAssisted)
  const selectedTagIds = useComposerStore((state) => state.selectedTagIds)
  const variant = useComposerStore((state) => state.channelVariants[channelId])

  const readFromOmni = bridgeReady

  // Which store serves the content fields:
  // - channel-variant reads active AND customize mode: the channel's variant;
  //   if the variant is missing (channel just selected, mirror not settled),
  //   fall back to the legacy snapshot — NOT omni, whose content is the shared
  //   channel-less draft, not this channel's.
  // - otherwise (omni mode, or channel-variant reads inactive): omni Zustand
  //   when the omni bridge is ready, else the legacy snapshot.
  const variantModeActive = channelReadsActive && !legacy.isOmniMode
  const activeVariant = variantModeActive ? variant : undefined
  const contentFromOmni = !variantModeActive && readFromOmni

  // No ChannelVariant field is nullish, so `?? ` cleanly means "variant absent".
  const text = activeVariant?.text ?? (contentFromOmni ? omniText : legacy.text)
  const urls = activeVariant?.urls ?? (contentFromOmni ? omniUrls : legacy.urls)
  const unshortenedUrls =
    activeVariant?.unshortenedUrls ??
    (contentFromOmni ? omniUnshortenedUrls : legacy.unshortenedUrls)
  const assets =
    activeVariant?.assets ?? (contentFromOmni ? omniAssets : legacy.assets)
  const aiAssisted =
    activeVariant?.aiAssisted ??
    (contentFromOmni ? omniAiAssisted : legacy.aiAssisted)

  // Unlike content, the metadata scalars and channelData live on the
  // per-service draft in both modes (see useLegacyChannelData.buildSnapshot),
  // so this reads the variant whenever channel reads are active — no
  // isOmniMode check.
  const serviceFieldsFromVariant = channelReadsActive && variant !== undefined
  const updateType = serviceFieldsFromVariant
    ? variant.updateType
    : legacy.updateType
  const isReminder = serviceFieldsFromVariant
    ? variant.isReminder
    : legacy.isReminder
  const title = serviceFieldsFromVariant ? variant.title : legacy.title
  const enabledAttachmentType = serviceFieldsFromVariant
    ? variant.enabledAttachmentType
    : legacy.enabledAttachmentType
  const channelData = serviceFieldsFromVariant
    ? variant.channelData
    : legacy.channelData
  const sourceLink = serviceFieldsFromVariant
    ? variant.sourceLink
    : legacy.sourceLink
  const retweet = serviceFieldsFromVariant ? variant.retweet : legacy.retweet
  const selectedStickers = serviceFieldsFromVariant
    ? variant.selectedStickers
    : legacy.selectedStickers
  const threadItems = serviceFieldsFromVariant
    ? variant.threadItems
    : legacy.threadItems

  return useMemo(
    (): DraftView => ({
      channelId,
      text,
      urls,
      unshortenedUrls,
      assets,
      aiAssisted,
      updateType,
      isReminder,
      title,
      enabledAttachmentType,
      channelData,
      sourceLink,
      retweet,
      selectedStickers,
      threadItems,
      // By design, not a migration gap: see the header comment.
      places: legacy.places,
      document: legacy.document,
      selectedTagIds,
    }),
    [
      channelId,
      text,
      urls,
      unshortenedUrls,
      assets,
      aiAssisted,
      updateType,
      isReminder,
      title,
      enabledAttachmentType,
      channelData,
      sourceLink,
      retweet,
      selectedStickers,
      threadItems,
      legacy,
      selectedTagIds,
    ],
  )
}
